import { EMPTY, Observable } from 'rxjs';
import { expand, reduce } from 'rxjs/operators';

import {
  ReadinessGap,
  ReadinessRow,
  ReadinessStatus,
} from '../../../../services/suburb-readiness.service';
import { SuburbTab } from '../workspace/panel';

/**
 * The readiness view's rules (#262): what each column shows, where each gap is fixed, and the CSV. Pure. What counts
 * as missing is the server's (row.missing), so the view and the "missing" filter can't disagree.
 */

export const GAP_LABELS: Record<ReadinessGap, string> = {
  published: 'Published',
  opening: 'Opening paragraph',
  about: 'About text',
  disposal: 'Crematoria and cemeteries',
  serviceVenues: 'Venues for a service',
  wakeVenues: 'Venues for a wake',
  families: 'Families helped',
  arrangers: 'Arrangers',
  faqs: 'FAQs',
  gate: 'Search check',
  owner: 'Owner',
};

export const MISSING_OPTIONS = (Object.keys(GAP_LABELS) as ReadinessGap[]).map((value) => ({
  label: GAP_LABELS[value],
  value,
}));

export const STATUS_OPTIONS: Array<{ label: string; value: ReadinessStatus }> = [
  { label: 'To do', value: 'todo' },
  { label: 'In progress', value: 'in-progress' },
  { label: 'Done', value: 'done' },
];

/** The columns that can be missing, in table order (owner is edited in its own column). */
export const GAP_COLUMNS: ReadonlyArray<ReadinessGap> = [
  'published',
  'opening',
  'about',
  'disposal',
  'serviceVenues',
  'wakeVenues',
  'families',
  'arrangers',
  'faqs',
  'gate',
];

/** Where a gap is fixed: a suburb's tab, a sub-region's panel, or the page editor of a record not on the map. */
export type GapTarget =
  | { kind: 'suburb'; salCode: string; tab: SuburbTab }
  | { kind: 'subregion'; id: string }
  | { kind: 'record'; recordId: string };

/** Families helped and arrangers come from arrangements and director profiles: no tab here fixes them. */
const NOT_HERE = new Set<ReadinessGap>(['families', 'arrangers', 'owner']);

const SUBURB_TAB: Record<Exclude<ReadinessGap, 'families' | 'arrangers' | 'owner'>, SuburbTab> = {
  published: 'overview',
  gate: 'overview',
  opening: 'page',
  about: 'page',
  faqs: 'page',
  disposal: 'places',
  serviceVenues: 'places',
  wakeVenues: 'places',
};

export function gapTarget(row: ReadinessRow, gap: ReadinessGap): GapTarget | null {
  if (NOT_HERE.has(gap)) return null;
  if (row.template === 'subregion') return { kind: 'subregion', id: row.recordId };
  if (!row.salCode) return { kind: 'record', recordId: row.recordId };
  const tab =
    gap === 'opening' && row.opening.draftsWaiting > 0
      ? 'drafts'
      : SUBURB_TAB[gap as keyof typeof SUBURB_TAB];
  return { kind: 'suburb', salCode: row.salCode, tab };
}

/** One cell: whether the page has it (null: not applicable here) and what to say. */
export interface ReadinessCell {
  ok: boolean | null;
  text: string;
}

export function cellOf(row: ReadinessRow, gap: ReadinessGap): ReadinessCell {
  const ok = !row.missing.includes(gap);
  const p = row.places;
  switch (gap) {
    case 'published':
      return { ok, text: row.published ? 'On the website' : 'Not published' };
    case 'opening':
      if (row.opening.state === 'written') return { ok, text: 'Written' };
      if (row.opening.state === 'draft')
        return { ok, text: `AI draft waiting (${row.opening.draftsWaiting})` };
      return { ok, text: row.opening.dataLead ? 'Empty (the site writes a lead)' : 'Empty' };
    case 'about':
      return { ok, text: row.about ? 'Written' : 'Empty' };
    case 'disposal':
      return p
        ? { ok, text: `${p.crematoria} crematoria, ${p.cemeteries} cemeteries` }
        : { ok: null, text: 'n/a' };
    case 'serviceVenues':
      return p ? { ok, text: String(p.serviceVenues) } : { ok: null, text: 'n/a' };
    case 'wakeVenues':
      return p ? { ok, text: String(p.wakeVenues) } : { ok: null, text: 'n/a' };
    case 'families':
      return row.template === 'subregion'
        ? { ok: null, text: 'n/a' }
        : { ok, text: row.families ?? 'None' };
    case 'arrangers':
      return { ok, text: arrangersText(row) };
    case 'faqs':
      return { ok, text: String(row.faqs) };
    case 'gate':
      return row.gate
        ? { ok, text: row.gate.pass ? 'Passes' : `Fails ${failedGates(row)}` }
        : { ok, text: 'Not in the last build' };
    case 'owner':
      return { ok, text: row.owner?.name ?? 'Nobody' };
  }
}

function arrangersText(row: ReadinessRow): string {
  const names = row.arrangers.shown.map((a) => a.name).join(', ') || 'None';
  return row.arrangers.unpublished
    ? `${names} (+${row.arrangers.unpublished} without a public profile)`
    : names;
}

function failedGates(row: ReadinessRow): string {
  const g = row.gate!;
  return (['g1', 'g2', 'g3', 'g4'] as const)
    .filter((k) => !g[k])
    .map((k) => k.toUpperCase())
    .join(', ');
}

// ------------------------------------------------------------------ CSV

/**
 * One CSV cell, quoted. A cell starting with = + - @ (or a tab or carriage return) gets a leading ' so a spreadsheet
 * shows it as text instead of running it as a formula: page names and headings are typed by people.
 */
export function csvCell(value: unknown): string {
  let text = value === null || value === undefined ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
}

export const CSV_HEADER = [
  'Page',
  'Kind',
  'Region',
  'Sub-regions',
  'Address',
  'Published',
  'Last published',
  'Opening paragraph',
  'AI drafts waiting',
  'About text',
  'Crematoria',
  'Cemeteries',
  'Venues for a service',
  'Venues for a wake',
  'Families helped',
  'Arrangers',
  'Arrangers without a public profile',
  'FAQs',
  'Inserted blocks',
  'Search check',
  'Owner',
  'Status',
  'Missing',
];

export interface CsvNames {
  region: (id: string) => string;
  subRegion: (id: string) => string;
}

/** The rows as a CSV file (CRLF lines, every cell quoted), in the columns above. */
export function readinessCsv(rows: ReadonlyArray<ReadinessRow>, names: CsvNames): string {
  const status = new Map(STATUS_OPTIONS.map((s) => [s.value, s.label]));
  const lines = rows.map((r) =>
    [
      r.name,
      r.kind ?? '',
      names.region(r.regionId),
      r.subRegionIds.map(names.subRegion).join('; '),
      r.url,
      r.published ? 'yes' : 'no',
      r.lastPublishedAt ?? '',
      r.opening.state,
      r.opening.draftsWaiting,
      r.about ? 'yes' : 'no',
      r.places?.crematoria ?? '',
      r.places?.cemeteries ?? '',
      r.places?.serviceVenues ?? '',
      r.places?.wakeVenues ?? '',
      r.families ?? '',
      r.arrangers.shown.map((a) => a.name).join('; '),
      r.arrangers.unpublished,
      r.faqs,
      r.insertedBlocks,
      r.gate ? (r.gate.pass ? 'passes' : 'fails') : '',
      r.owner?.name ?? '',
      status.get(r.status) ?? r.status,
      r.missing.map((m) => GAP_LABELS[m] ?? m).join('; '),
    ]
      .map(csvCell)
      .join(',')
  );
  return [CSV_HEADER.map(csvCell).join(','), ...lines].join('\r\n') + '\r\n';
}

/**
 * Every row of a filtered list, page after page in turn (the server pages at 200 at most), for the CSV. Stops at the
 * total, or at an empty page, so a list that shrinks while it is read can't loop.
 */
export function fetchAllPages<T>(
  fetch: (offset: number) => Observable<{ offset: number; total: number; rows: T[] }>
): Observable<T[]> {
  return fetch(0).pipe(
    expand((p) =>
      p.rows.length && p.offset + p.rows.length < p.total ? fetch(p.offset + p.rows.length) : EMPTY
    ),
    reduce((all, p) => all.concat(p.rows), [] as T[])
  );
}
